/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useState } from 'react';
import { Grid } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { REPORT_TYPE } from 'modules/dashboard/constants';
import { selectEmbedToken, selectHotelName, selectTabList } from 'modules/dashboard/selectors';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { getVisualName } from 'modules/dashboard/functions';
import VisualCard from 'modules/dashboard/components/tab-container/visual-card';
import { CustomToggleSwitch } from 'modules/common/components';
import { ENVIRONMENT } from 'config';
import PaceFilter from '../../filter-panel';
import {
  selectActivePaceTab,
  selectPaceTimeRevenueFilterList,
  selectPaceTimeVisuals,
  selectTabularView,
  selectIsFocusOnSet,
  selectIsPaceSegmentTimeDeAggregate,
  selectHotelFocusOn,
} from '../../../selectors';
import PaceTimeTabularWidget from './pace-time/tabular-visual-view';
import PaceTimeGraphicalWidget from './pace-time/graphical-visual-view';
import { paceActions } from '../../../slice';
import PaceHotelGraphicalWidget from './pace-hotel/graphical-visual-view';
import PaceHotelTabularWidget from './pace-hotel/tabular-visual-view';
import PaceSegmentGraphicalWidget from './pace-segment/graphical-visual-view';
import PaceSegmentTabularWidget from './pace-segment/tabular-visual-view';
import PaceTimeSegmentTabularWidget from './pace-segment-time/tabular-visual-view';
import PaceTimeSegmentGraphicalWidget from './pace-segment-time/graphical-visual-view';
import PaceHotelSegmentTabularWidget from './pace-segment-hotel/tabular-visual-view';
import PaceHotelSegmentGraphicalWidget from './pace-segment-hotel/graphical-visual-view';
import CeruleanPaceTimeTabularWidget from './pace-time/cerulean-tabular-visual-view';
import CeruleanPaceHotelTabularWidget from './pace-hotel/cerulean-tabular-visual-view';
import CeruleanPaceSegmentTabularWidget from './pace-segment/cerulean-tabular-visual-view';
import CeruleanPaceTimeSegmentTabularWidget from './pace-segment-time/cerulean-tabular-time-segment-visual-view';
import CeruleanPaceHotelSegmentTabularWidget from './pace-segment-hotel/cerulean-tabular-hotel-segment-visual-view';

/**
 * Trend tab widget implementation
 * @param {Number} value - Trend time widget tab value
 * @param {Boolean} toggleDisabled - PMS sync toggle disabled state
 * @param {Date} latestDate - Latest booking date of selected hotel
 * @param {String} hotelTimezone - Timezone of selected hotel
 * @param {String} reportType - Report type of active tab
 * @returns
 */
const Widget = ({ value, latestDate, reportType, hotelTimezone, toggleDisabled }) => {
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const filterList = useSelector(selectPaceTimeRevenueFilterList);
  const tokenDetails = useSelector(selectEmbedToken);
  const visuals = useSelector(selectPaceTimeVisuals);
  const activeTab = useSelector(selectActivePaceTab);
  const tabularView = useSelector(selectTabularView);
  const IsFocusOnSet = useSelector(selectIsFocusOnSet);
  const isPaceSegmentTimeDeAggregate = useSelector(selectIsPaceSegmentTimeDeAggregate);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const selectedHotel = useSelector(selectHotelName);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const [isLoaded, setIsLoaded] = useState(false);

  // To get visuals based on the configuration
  const getWidget = (index, cssClassName) => {
    const found = visuals?.find((visual) => visual?.order === index);
    //
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={tokenDetails?.accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filterList}
          tab={TAB_VALUE.PACE}
        />
      )
    );
  };
  //
  const paceTimeVisual =
    ENVIRONMENT?.ENABLE_PACE_TIME_JS_TABLES?.toLowerCase() === 'true' ? (
      <CeruleanPaceTimeTabularWidget setIsTimeLoaded={setIsLoaded} isLoaded={isLoaded} />
    ) : (
      <PaceTimeTabularWidget setIsTimeLoaded={setIsLoaded} />
    );
  const paceHotelVisual =
    ENVIRONMENT?.ENABLE_PACE_HOTEL_JS_TABLES?.toLowerCase() === 'true' ? (
      <CeruleanPaceHotelTabularWidget setIsHotelLoaded={setIsLoaded} isLoaded={isLoaded} />
    ) : (
      <PaceHotelTabularWidget setIsHotelLoaded={setIsLoaded} />
    );
  const paceSegmentVisual =
    ENVIRONMENT?.ENABLE_PACE_SEGMENT_JS_TABLES?.toLowerCase() === 'true' ? (
      <CeruleanPaceSegmentTabularWidget setIsSegmentLoaded={setIsLoaded} isLoaded={isLoaded} />
    ) : (
      <PaceSegmentTabularWidget setIsSegmentLoaded={setIsLoaded} />
    );
  // Every combination is served by a JS table
  const paceHotelSegmentVisual =
    ENVIRONMENT?.ENABLE_PACE_HOTEL_SEGMENT_JS_TABLES?.toLowerCase() === 'true' ? (
      <CeruleanPaceHotelSegmentTabularWidget setIsSegmentLoaded={setIsLoaded} isLoaded={isLoaded} />
    ) : (
      <PaceHotelSegmentTabularWidget setIsSegmentLoaded={setIsLoaded} />
    );
  //
  const paceTimeSegmentVisual =
    ENVIRONMENT?.ENABLE_PACE_TIME_SEGMENT_JS_TABLES?.toLowerCase() === 'true' ? (
      <CeruleanPaceTimeSegmentTabularWidget setIsSegmentLoaded={setIsLoaded} isLoaded={isLoaded} />
    ) : (
      <PaceTimeSegmentTabularWidget setIsSegmentLoaded={setIsLoaded} />
    );
  //
  return (
    <>
      <Grid
        item
        xs={2}
        cc={2.5}
        md={2}
        sx={{
          ml: {
            xs: 0,
            cc: 0.25,
            md: 0,
          },
        }}
      >
        <PaceFilter
          reportType={reportType}
          tabValue={value}
          ownTabValue={TAB_VALUE.PACE}
          toggleDisabled={toggleDisabled}
          latestDate={latestDate}
          hotelTimezone={hotelTimezone}
          isLoaded={isLoaded}
          visual={getWidget(100, styles.symmetricComparisonWindow)}
          visualName={getVisualName(visuals, 100)}
        />
      </Grid>
      {IsFocusOnSet && (
        <Grid container item xs={10} cc={9.25} md={10} direction="column">
          <Grid item container justifyContent="flex-end" xs={12}>
            <CustomToggleSwitch
              onChange={(event) => dispatch(paceActions.setTabularView(event.target.checked))}
              label="Tabular View"
              checked={tabularView}
              paddingBottom={1}
              className="tabular-view-toggle"
            />
          </Grid>
          {activeTab === REPORT_TYPE.PACE_TIME_WIDGET &&
            !isPaceSegmentTimeDeAggregate &&
            (tabularView ? (
              paceTimeVisual
            ) : (
              <PaceTimeGraphicalWidget setIsTimeLoaded={setIsLoaded} />
            ))}
          {activeTab === REPORT_TYPE.PACE_SEGMENT_WIDGET &&
            isPaceSegmentTimeDeAggregate &&
            hotelFocusOn?.id !== selectedHotel?.id &&
            (tabularView ? (
              paceHotelSegmentVisual
            ) : (
              <PaceHotelSegmentGraphicalWidget setIsSegmentLoaded={setIsLoaded} />
            ))}
          {activeTab === REPORT_TYPE.PACE_SEGMENT_WIDGET &&
            isPaceSegmentTimeDeAggregate &&
            hotelFocusOn?.id === selectedHotel?.id &&
            (tabularView ? (
              paceTimeSegmentVisual
            ) : (
              <PaceTimeSegmentGraphicalWidget setIsSegmentLoaded={setIsLoaded} />
            ))}
          {activeTab === REPORT_TYPE.PACE_SEGMENT_WIDGET &&
            !isPaceSegmentTimeDeAggregate &&
            (tabularView ? (
              paceSegmentVisual
            ) : (
              <PaceSegmentGraphicalWidget setIsSegmentLoaded={setIsLoaded} />
            ))}
          {activeTab === REPORT_TYPE.PACE_HOTEL_WIDGET &&
            (tabularView ? (
              paceHotelVisual
            ) : (
              <PaceHotelGraphicalWidget setIsHotelLoaded={setIsLoaded} />
            ))}
        </Grid>
      )}
    </>
  );
};
//
export default Widget;
